import { Flag } from "../Flags";
import * as Utils from "../util/Utils";
import * as StringUtils from "../util/StringUtils";
import * as DownloadUtils from "../util/DownloadUtils";
import React, {
	DependencyList,
	Dispatch,
	EffectCallback,
	Ref,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import * as StorageUtils from "../util/StorageUtils";
import { StorageKey } from "../util/StorageUtils";
import useDimensionsImpl from "react-cool-dimensions";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { getFirebaseAuth, getFirebaseNamespace } from "../FirebaseClient";
import { getPortalElement } from "../util/ReactUtils";
import ReactDOM from "react-dom";

export type RefCallback<T> = Ref<T> | ((current: T | null) => void);

export const useFlag = <T>(flag: Flag<T>): Flag<T> => {
	const [_, setIter] = useState<number>(0);
	useEffect(() => {
		const listener = () => setIter((iter) => iter + 1);
		flag.addListener(listener);
		return () => flag.removeListener(listener);
	}, []); // eslint-disable-line
	return flag;
};

export const useMemoDebug = <T>(
	factory: () => T,
	deps: DependencyList | undefined,
	enabled = true,
): T => {
	const previousDeps = useRef<readonly any[] | undefined>(undefined);
	// eslint-disable-next-line
	const value = useMemo(factory, deps);
	if (
		enabled &&
		process.env.NODE_ENV === "development" &&
		previousDeps.current &&
		!Utils.shallowEqual(previousDeps.current, deps, { log: false })
	) {
		// eslint-disable-next-line no-console
		console.trace("Re-rendered", {
			prev: previousDeps.current,
			next: deps,
		});
		previousDeps.current = deps;
	}
	return value;
};

export const useMemoDeep = <T>(
	factory: () => T,
	deps: DependencyList | undefined,
) => {
	const previousDepsRef = useRef<readonly any[] | undefined>(undefined);
	const previousValueRef = useRef<T>();
	const memoFunc = useCallback(
		(deps: readonly any[] | undefined) => {
			if (!Utils.deepEqual(previousDepsRef.current, deps)) {
				return (previousValueRef.current = factory());
			}
			return previousValueRef.current;
		},
		[factory],
	);
	const value = memoFunc(deps);
	previousDepsRef.current = deps;
	return value;
};

export const useOnceImmediate = (func: Function) => {
	const used = useRef(false);
	if (!used.current) {
		used.current = true;
		func();
	}
};

export const useObservable = <T>(
	observed: () => void | Promise<void>,
): [RefCallback<HTMLElement>] => {
	const elementRef = useRef<HTMLElement | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const cleanUpObserver = useCallback(() => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
	}, []);

	useEffect(() => cleanUpObserver, [cleanUpObserver]);

	useEffect(() => {
		DownloadUtils.pdfLoadableElements.push(observed);
		return () => {
			DownloadUtils.pdfLoadableElements.splice(
				DownloadUtils.pdfLoadableElements.indexOf(observed),
				1,
			);
		};
	}, [observed]);

	const setupObserver = useCallback(() => {
		cleanUpObserver();
		if (elementRef.current) {
			const observer = (observerRef.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						observed();
					}
				},
			));
			observer.observe(elementRef.current);
		}
	}, [cleanUpObserver, observed]);

	const refCollector: RefCallback<HTMLElement> = useCallback(
		(element: HTMLElement) => {
			elementRef.current = element;
			if (element) {
				setupObserver();
			}
		},
		[setupObserver],
	);

	return [refCollector];
};

export type CallbackRef<T extends (...args: any[]) => any> = { run: T };

export const useCallbackRef = <T extends (...args: any[]) => any>(
	func: T,
	depList: DependencyList,
): [T, CallbackRef<T>] => {
	// eslint-disable-next-line
	const callback = useCallback(func, depList);
	const ref = useRef({ run: callback });
	ref.current.run = callback;
	return [callback, ref.current];
};

export const useAutoUpdateCallback = <F extends (...args: any[]) => any>(
	callback: F,
	deps: DependencyList,
): F => {
	const ref = useRef<F>(callback);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	ref.current = useCallback(callback, deps);
	return useCallback(
		(...args: any[]) => ref.current.apply(null, args),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		deps,
	) as F;
};

export const useDebouncedMemo = <T>(
	factory: () => T,
	deps: DependencyList | undefined,
	debounce: number,
): T => {
	const [state, setState] = useState(factory());

	// eslint-disable-next-line
	const debouncedSetState = useCallback(Utils.debounce(setState, debounce), []);

	useEffect(() => {
		debouncedSetState(factory());
		// eslint-disable-next-line
	}, deps);

	return state;
};

export const useDebouncedCallback = <F extends (...args: any) => any>(
	callback: F,
	deps: DependencyList | undefined,
	debounce: number,
): F => {
	// eslint-disable-next-line
	return useCallback(Utils.debounce(callback, debounce), [
		...(deps || []),
		debounce,
	]) as any;
};

export const useDebouncedEffect = (
	effect: () => void,
	deps: DependencyList | undefined,
	debounce: number,
	immediate?: () => void,
): void => {
	const effectRef = useRef<() => void>();
	effectRef.current = effect;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const runEffect = useCallback(
		Utils.debounce(() => {
			effectRef.current?.call(null);
		}, debounce),
		[debounce],
	);

	useEffect(() => {
		if (immediate) {
			immediate();
		}
		runEffect();
		// eslint-disable-next-line
	}, deps);
};

export const useForceUpdate = (): [() => void, number] => {
	const [key, setKey] = useState(0);

	const forceUpdate = useCallback(() => {
		setKey((i) => i + 1);
	}, [setKey]);

	return [forceUpdate, key];
};

export const useStorageState = <T>(
	key: StorageKey<T> | undefined,
): [T | undefined, (value: T | undefined) => void] => {
	const [value, _setValue] = useState<T | undefined>(
		key ? StorageUtils.get(key) : undefined,
	);

	const setValue = useCallback(
		(value: T | undefined) => {
			if (key) {
				StorageUtils.set(key, value);
			}
		},
		[key],
	);

	useEffect(() => {
		if (key) {
			const listener = StorageUtils.addListener<T>((updatedKey, value) => {
				if (updatedKey.id === key.id) {
					_setValue(value);
				}
			});
			return () => {
				StorageUtils.removeListener(listener);
			};
		}
	}, [key]);

	return [value, setValue];
};

export const useStateEx = <S>(
	initialState?: S | (() => S),
): {
	state: S;
	setState: Dispatch<SetStateAction<S>>;
	getState: () => S;
	initialState: S | undefined;
	resetState: () => void;
} => {
	const [state, _setState] = useState(initialState);
	const initialStateRef = useRef<any>(Utils.deepClone(state));
	const stateRef = useRef<any>(state);
	stateRef.current = state;
	const getState = useCallback(() => stateRef.current, []);

	// override default setState in order to to get the newValue immediately within the getter
	const setState = useCallback(
		(action: SetStateAction<S>) => {
			const newValue =
				typeof action === "function" ? (action as any)(getState()) : action;
			stateRef.current = newValue;
			_setState(newValue);
		},
		[getState],
	);

	const resetState = useCallback(() => {
		setState(initialStateRef.current);
	}, [setState]);

	return {
		state: state as any,
		setState: setState,
		getState: getState,
		initialState: initialStateRef.current,
		resetState: resetState,
	};
};

export const useMountEffect = (effect: EffectCallback) => {
	// eslint-disable-next-line
	useEffect(effect, []);
};

export const useCallableEffect = <F extends EffectCallback>(
	effect: F,
	depList: DependencyList,
): F => {
	// eslint-disable-next-line
	const func = useCallback(effect, depList);
	useEffect(func, [func]);
	return func;
};

export const usePrevious = <T>(value: T): [T, () => T] => {
	const ref = useRef<T>(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	const getter = useCallback(() => ref.current, []);

	return [ref.current, getter];
};

export const useBoolean = (
	initialState: boolean = false,
): {
	state: boolean;
	setState: Dispatch<SetStateAction<boolean>>;
	toggle: () => void;
	forceTrue: () => void;
	forceFalse: () => void;
} => {
	const [state, setState] = useState<boolean>(initialState);
	const toggle = useCallback(() => setState((state) => !state), []);

	const forceTrue = useCallback(() => setState(true), []);
	const forceFalse = useCallback(() => setState(false), []);

	return { state, setState, toggle, forceTrue, forceFalse };
};

export const useDimensions: () => [
	React.Ref<any>,
	{ width: number; height: number },
] = () => {
	const result = useDimensionsImpl({
		useBorderBoxSize: true,
	});
	return [result.ref, result];
};

export const useRecaptchaVerifier = (): [
	React.ReactNode,
	() => Promise<string>,
] => {
	const id = useRef(`recaptcha-${StringUtils.uuid()}`).current;
	const element = useMemo(
		() =>
			ReactDOM.createPortal(
				React.createElement("div", { id }),
				getPortalElement("recaptcha"),
			),
		[id],
	);

	const recaptchaVerifier = useRef<firebase.auth.RecaptchaVerifier>();

	const loadRecaptchaVerifier =
		useCallback(async (): Promise<firebase.auth.RecaptchaVerifier> => {
			if (recaptchaVerifier.current) {
				(recaptchaVerifier.current as any).reset();
				return recaptchaVerifier.current;
			}
			const firebase = await getFirebaseNamespace();
			await getFirebaseAuth();
			return (recaptchaVerifier.current = new firebase.auth.RecaptchaVerifier(
				id,
				{
					size: "invisible",
				},
			));
		}, [id]);

	const verify = useCallback(async () => {
		const verifier = await loadRecaptchaVerifier();
		return verifier.verify();
	}, [loadRecaptchaVerifier]);

	return [element, verify];
};

export const useGetter = <T>(input: T): (() => T) => {
	const inputRef = useRef(input);
	inputRef.current = input;
	return useCallback(() => inputRef.current, []);
};

export const useChangedEffect = (
	effect: EffectCallback,
	depList: DependencyList,
) => {
	const [previous] = usePrevious(depList);

	useEffect(() => {
		if (previous) {
			return effect();
		}
		// eslint-disable-next-line
	}, depList);
};
